import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { WorkflowComposerApi } from './WorkflowComposerApi';
import type {
  DecomposeRequest,
  DecomposeResponse,
  ClarifyRequest,
  WorkflowStatus,
  FactoryGenerateRequest,
  FactoryGenerateResponse,
  FactoryPipelineStatus,
  FactoryBuildResponse,
  FactoryDeployResponse,
  FactoryValidateResponse,
  ExecutionRecord,
} from './types';

export class WorkflowComposerClient implements WorkflowComposerApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async proxyUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('proxy');
  }

  private async decomposerUrl(): Promise<string> {
    return `${await this.proxyUrl()}/decomposer`;
  }

  private async factoryUrl(): Promise<string> {
    return `${await this.proxyUrl()}/agent-factory`;
  }

  private async historyUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('workflow-composer-history');
  }

  /* ── Decomposer ──────────────────────────────────────────────────────── */

  async decompose(request: DecomposeRequest): Promise<DecomposeResponse> {
    const base = await this.decomposerUrl();
    const resp = await this.fetchApi.fetch(`${base}/decompose`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!resp.ok) {
      throw new Error(`Decompose failed (HTTP ${resp.status})`);
    }
    return resp.json();
  }

  async clarify(
    workflowId: string,
    request: ClarifyRequest,
  ): Promise<DecomposeResponse> {
    const base = await this.decomposerUrl();
    const resp = await this.fetchApi.fetch(
      `${base}/clarify/${encodeURIComponent(workflowId)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      },
    );
    if (!resp.ok) {
      throw new Error(`Clarify failed (HTTP ${resp.status})`);
    }
    return resp.json();
  }

  async getWorkflowStatus(workflowId: string): Promise<WorkflowStatus> {
    const base = await this.decomposerUrl();
    const resp = await this.fetchApi.fetch(
      `${base}/status/${encodeURIComponent(workflowId)}`,
    );
    if (!resp.ok) {
      throw new Error(`Status fetch failed (HTTP ${resp.status})`);
    }
    return resp.json();
  }

  /* ── Agent Factory ───────────────────────────────────────────────────── */

  async generateAgent(
    request: FactoryGenerateRequest,
  ): Promise<FactoryGenerateResponse> {
    const base = await this.factoryUrl();
    const resp = await this.fetchApi.fetch(`${base}/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!resp.ok) {
      throw new Error(`Generate failed (HTTP ${resp.status})`);
    }
    return resp.json();
  }

  async buildAgent(agentName: string): Promise<FactoryBuildResponse> {
    const base = await this.factoryUrl();
    const resp = await this.fetchApi.fetch(
      `${base}/build/${encodeURIComponent(agentName)}`,
      { method: 'POST' },
    );
    if (!resp.ok) {
      throw new Error(`Build failed (HTTP ${resp.status})`);
    }
    return resp.json();
  }

  async deployAgent(agentName: string): Promise<FactoryDeployResponse> {
    const base = await this.factoryUrl();
    const resp = await this.fetchApi.fetch(
      `${base}/deploy/${encodeURIComponent(agentName)}`,
      { method: 'POST' },
    );
    if (!resp.ok) {
      throw new Error(`Deploy failed (HTTP ${resp.status})`);
    }
    return resp.json();
  }

  async validateAgent(agentName: string): Promise<FactoryValidateResponse> {
    const base = await this.factoryUrl();
    const resp = await this.fetchApi.fetch(
      `${base}/validate/${encodeURIComponent(agentName)}`,
      { method: 'POST' },
    );
    if (!resp.ok) {
      throw new Error(`Validate failed (HTTP ${resp.status})`);
    }
    return resp.json();
  }

  async getFactoryStatus(agentName: string): Promise<FactoryPipelineStatus> {
    const base = await this.factoryUrl();
    const resp = await this.fetchApi.fetch(
      `${base}/status/${encodeURIComponent(agentName)}`,
    );
    if (!resp.ok) {
      throw new Error(`Factory status failed (HTTP ${resp.status})`);
    }
    return resp.json();
  }

  /* ── Execution History ───────────────────────────────────────────────── */

  async listExecutions(): Promise<ExecutionRecord[]> {
    const base = await this.historyUrl();
    const resp = await this.fetchApi.fetch(`${base}/executions`);
    if (!resp.ok) {
      throw new Error(`List executions failed (HTTP ${resp.status})`);
    }
    return resp.json();
  }

  async saveExecution(
    record: Omit<ExecutionRecord, 'createdAt' | 'updatedAt'>,
  ): Promise<void> {
    const base = await this.historyUrl();
    const resp = await this.fetchApi.fetch(`${base}/executions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(record),
    });
    if (!resp.ok) {
      throw new Error(`Save execution failed (HTTP ${resp.status})`);
    }
  }

  async deleteExecution(id: string): Promise<void> {
    const base = await this.historyUrl();
    const resp = await this.fetchApi.fetch(
      `${base}/executions/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
    if (!resp.ok) {
      throw new Error(`Delete execution failed (HTTP ${resp.status})`);
    }
  }
}
